import React, { ReactNode } from 'react';
import { useDecision } from '@optimizely/react-sdk';

type FeatureFlagProps = {
  featureKey: string;
  variable?: string;
  children: ReactNode;
  fallback?: ReactNode;
  loadingFallback?: ReactNode;
};

/**
 * Higher Order Component that conditionally renders children based on feature flag status
 * @param featureKey - The Optimizely feature key to check
 * @param children - Content to render if feature is enabled
 * @param fallback - Optional content to render if feature is disabled (defaults to null)
 * @param loadingFallback - Optional content to render while the Optimizely client is
 *   fetching its datafile; until then the decision is not trustworthy (defaults to null)
 */
export const FeatureFlag: React.FC<FeatureFlagProps> = ({
  featureKey,
  variable,
  children,
  fallback = null,
  loadingFallback = null,
}) => {
  const [decision, clientReady] = useDecision(featureKey);

  if (!clientReady) {
    return <>{loadingFallback}</>;
  }

  if (variable) {
    const vars = decision?.variables;
    const isVariableEnabled = Boolean(vars?.[variable]);
    return isVariableEnabled ? <>{children}</> : <>{fallback}</>;
  }

  if (decision.enabled) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};
